import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Tag,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  Badge,
} from '@chakra-ui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  DownloadIcon,
  AddIcon,
  DeleteIcon,
  ExternalLinkIcon,
  ArrowBackIcon,
} from '@chakra-ui/icons';
import { FiRefreshCw } from 'react-icons/fi';
import { useFindMany, useAction, useActionForm, useGlobalAction, useUser } from '@gadgetinc/react';
import { api } from '../api';
import { getUserRole, canUserAccessShift, isInstructor, isScheduler, isStudent, hasAnyRole, isInstitutionAdministrator } from '../lib/roleUtils';

const SHIFT_TYPE_COLORS = {
  field: 'blue',
  hospital: 'green', 
  lab: 'purple',
  field_internship: 'orange',
};

const STATUS_COLORS = {
  scheduled: 'gray',
  in_progress: 'yellow',
  completed: 'green',
  cancelled: 'red',
};

const VIEWS = {
  MONTH: 'month',
  WEEK: 'week', 
  DAY: 'day',
  LIST: 'list',
};

const QUICK_FILTERS = {
  MY_SHIFTS: 'my_shifts',
  THIS_WEEK: 'this_week',
  THIS_MONTH: 'this_month',
  FISDAP: 'fisdap',
};

const getRoleNames = (roles) => {
  if (!roles) return [];
  const rawRoles = Array.isArray(roles) ? roles : [roles];
  return rawRoles
    .map((role) => {
      if (typeof role === "string") return role;
      if (role && typeof role === "object") return role.name || role.key || role.role;
      return "";
    })
    .map((role) => String(role || "").trim().toLowerCase())
    .filter(Boolean);
};

const isSystemAdminUser = (user) => {
  if (!user) return false;
  if (user.email === "74spartan@gmail.com") return true;
  const roles = getRoleNames(user.roles || user.roleList);
  return roles.some((role) => ["system-admin", "system_admin", "sysadmin"].includes(role));
};

const formatClinicalSiteUnit = (unit) => {
  if (typeof unit === "string") return unit;
  if (unit && typeof unit === "object") return unit.name || unit.label || unit.type || "Unit";
  return "";
};

const recordMatchesInstitution = (record, institution) => {
  if (!institution) return true;
  if (record?.originalInstitution === institution) return true;
  if (Array.isArray(record?.sharedWithInstitutions) && record.sharedWithInstitutions.includes(institution)) return true;
  return Array.isArray(record?.institutionalAffiliations) && record.institutionalAffiliations.includes(institution);
};

const getStudentDisplayName = (student) => {
  const name = `${student?.firstName || ""} ${student?.lastName || ""}`.trim();
  return name || student?.email || "Unknown student";
};

// Helper function to format datetime strings for API compatibility
const formatDateTimeForAPI = (dateTimeString) => {
  if (!dateTimeString) return null;
  // Ensure the format includes seconds (YYYY-MM-DDTHH:MM:SS)
  if (dateTimeString.length === 16) {
    return dateTimeString + ':00';
  }
  return dateTimeString;
};

export default function Calendar() {
  const toast = useToast();
  const user = useUser(api);
  const navigate = useNavigate();
  const { isOpen: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure();
  const { isOpen: isShiftDetailOpen, onOpen: onShiftDetailOpen, onClose: onShiftDetailClose } = useDisclosure();
  const { isOpen: isAddShiftOpen, onOpen: onAddShiftOpen, onClose: onAddShiftClose } = useDisclosure();
  
  // Theme variables
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const sectionHeaderBg = useColorModeValue('gray.100', 'gray.700');
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(VIEWS.MONTH);
  const [selectedShift, setSelectedShift] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    institution: '',
    cohort: '',
    shiftType: '',
    status: '',
    agency: '',
    clinicalSite: '', 
    student: '',
    source: '',
    quickFilter: null,
  });
  
  // Track if we've already set the default filters to prevent loops
  const hasSetDefaultFilters = useRef(false);

  // Institution configuration state
  const [institutionConfig, setInstitutionConfig] = useState(null);
  const [configFetching, setConfigFetching] = useState(false);

  const canSeeAllInstitutions = isSystemAdminUser(user);
  const hasInstitutionAdminAccess = isInstitutionAdministrator(user);
  const canManageCalendar = isScheduler(user) || isInstructor(user) || hasInstitutionAdminAccess || hasAnyRole(user?.roles || user?.roleList, ['admin', 'system-admin']);
  const scopedInstitution = filters.institution || (canSeeAllInstitutions ? "" : user?.institution || "");
  
  // Add shift form - moved here before useEffects that depend on it
  const [newShiftData, setNewShiftData] = useState({
    agencyId: '',
    clinicalSiteId: '',
    shiftDate: '',
    endTime: '',
    shiftType: 'field',
    cohort: '',
    institution: '',
    location: '',
    unit: '',
    maxStudents: 1,
    enrollmentPeriodStart: '',
    enrollmentPeriodEnd: '',
    addStudent: false,
    selectedStudents: [],
    requiresApproval: false,
  });
  
  // Fetch institution configuration action
  const [{ data: configData, fetching: configActionFetching }, getInstitutionalConfig] = useGlobalAction(api.getInstitutionalConfig);

  // Set default institution and cohort filters when user loads
  useEffect(() => {
    if (user && user.institution && !canSeeAllInstitutions && filters.institution === '' && !hasSetDefaultFilters.current) {
      console.log('Setting default filters for user:', user.institution);
      
      // Set default cohort from activeCohorts or fallback to cohort
      let defaultCohort = '';
      if (user.activeCohorts && Array.isArray(user.activeCohorts) && user.activeCohorts.length > 0) {
        defaultCohort = user.activeCohorts[0];
      } else if (user.cohort?.name) {
        defaultCohort = user.cohort.name;
      }
      
      setFilters(prev => ({ 
        ...prev, 
        institution: user.institution,
        cohort: defaultCohort
      }));
      hasSetDefaultFilters.current = true;
    }
  }, [user, canSeeAllInstitutions, filters.institution]);

  // Fetch institution configuration when institution filter changes
  useEffect(() => {
    if (filters.institution) {
      setConfigFetching(true);
      getInstitutionalConfig({ institution: filters.institution })
        .then((result) => {
          if (result?.result) {
            setInstitutionConfig(result.result);
          }
          setConfigFetching(false);
        })
        .catch((error) => {
          console.error('Error fetching institution config:', error);
          setInstitutionConfig(null);
          setConfigFetching(false);
        });
    } else {
      setInstitutionConfig(null);
    }
  }, [filters.institution, getInstitutionalConfig]);
  
  const cohortQueryFilter = useMemo(() => {
    const next = { isActive: { equals: true } };
    if (scopedInstitution) {
      next.institution = { equals: scopedInstitution };
    }
    return next;
  }, [scopedInstitution]);

  const [{ data: cohorts, fetching: cohortsFetching }] = useFindMany(api.cohort, {
    filter: cohortQueryFilter,
    select: {
      id: true,
      name: true,
      displayName: true,
      institution: true,
    },
    sort: { name: "Ascending" },
    first: 250,
  });

  const resolveCohortId = useCallback((value) => {
    if (value == null) return null;
    const raw = typeof value === "object" ? (value.id || value.name || value.displayName) : value;
    const str = String(raw || "").trim();
    if (!str) return null;

    const directMatch = (cohorts || []).find((c) => String(c.id) === str);
    if (directMatch) return String(directMatch.id);

    const nameMatch = (cohorts || []).find(
      (c) => c.name === str || c.displayName === str
    );
    if (nameMatch) return String(nameMatch.id);

    if (/^[A-Za-z0-9_-]+$/.test(str)) return str;
    return null;
  }, [cohorts]);

  const userCohortIds = useMemo(() => {
    if (!user) return [];
    const raw = [];

    if (Array.isArray(user.activeCohorts)) {
      raw.push(...user.activeCohorts);
    }
    if (user.cohort) {
      raw.push(user.cohort);
    }

    const normalized = raw
      .map((entry) => resolveCohortId(entry))
      .filter(Boolean);

    return [...new Set(normalized)];
  }, [user, resolveCohortId]);

  useEffect(() => {
    if (!filters.cohort || filters.cohort === "all") return;
    const normalizedCohortId = resolveCohortId(filters.cohort);
    if (normalizedCohortId && normalizedCohortId !== filters.cohort) {
      setFilters((prev) => ({ ...prev, cohort: normalizedCohortId }));
    }
  }, [filters.cohort, resolveCohortId]);

  // Build API filter from state with role-based access control
  const apiFilter = useMemo(() => {
    console.log('=== DEBUG: Building API Filter ===');
    console.log('User:', user);
    console.log('User roles:', user?.roles);
    console.log('Is admin?', user?.roles?.includes('admin') || user?.roles?.includes('system-admin'));
    
    let filter = {};
    
    // Role-based filtering (skip for admin and system-admin users)
    const isAdminUser = canSeeAllInstitutions;
    console.log('Is admin user:', isAdminUser);
    
    // Check if "all" cohort filter is selected to bypass cohort restrictions
    const bypassCohortFilter = filters.cohort === 'all';
    
    if (user && !isAdminUser) {
      console.log('Applying role-based filtering...');
      const userRole = getUserRole(user);
      console.log('User role:', userRole);
      
      if (isStudent(user) && !bypassCohortFilter) {
        console.log('Applying student filtering...');
        // Students only see shifts for their cohort(s)
        if (userCohortIds.length === 1) {
          filter.cohortId = { equals: userCohortIds[0] };
          console.log('Student cohort filter:', userCohortIds[0]);
        } else if (userCohortIds.length > 1) {
          filter.cohortId = { in: userCohortIds };
          console.log('Student cohort filter:', userCohortIds);
        }
        // Show only non-completed shifts for students
        filter.status = { notEquals: 'completed' };
      } else if (isInstructor(user) && !bypassCohortFilter) {
        console.log('Applying instructor filtering...');
        // Instructors see shifts for their assigned cohorts (non-completed only)
        if (userCohortIds.length === 1) {
          filter.cohortId = { equals: userCohortIds[0] };
          console.log('Instructor cohort filter:', userCohortIds[0]);
        } else if (userCohortIds.length > 1) {
          filter.cohortId = { in: userCohortIds };
          console.log('Instructor cohort filter:', userCohortIds);
        }
        filter.status = { notEquals: 'completed' };
      } else if (isScheduler(user) || isInstitutionAdministrator(user)) {
        console.log('Applying scheduler filtering...');
        // Schedulers and institutional admins can see all shifts for their institution.
        if (user.institution) {
          filter.institution = { equals: user.institution };
          console.log('Institution scoped filter:', user.institution);
        }
      }
      
      // Apply status filter for students and instructors when bypass is enabled
      if (bypassCohortFilter && (isStudent(user) || isInstructor(user))) {
        filter.status = { notEquals: 'completed' };
      }
    } else {
      console.log('Skipping role-based filtering (admin user or no user)');
    }
    
    // Apply user filters on top of role-based filters
    if (filters.institution) {
      filter.institution = { equals: filters.institution };
    } else if (user?.institution && !canSeeAllInstitutions) {
      filter.institution = { equals: user.institution };
    }
    
    if (filters.cohort && filters.cohort !== 'all') {
      filter.cohortId = { equals: filters.cohort };
    }
    
    if (filters.shiftType) {
      filter.shiftType = { equals: filters.shiftType };
    }
    
    if (filters.status) {
      filter.status = { equals: filters.status };
    }

    if (filters.source) {
      filter.syncSource = { equals: filters.source };
    }
    
    if (filters.agency) {
      filter.agency = { equals: filters.agency };
    }
    
    if (filters.clinicalSite) {
      filter.clinicalSite = { equals: filters.clinicalSite };
    }
    
    // Date range filtering based on view and current date
    const startOfPeriod = new Date(currentDate);
    const endOfPeriod = new Date(currentDate);
    
    if (view === VIEWS.MONTH || view === VIEWS.LIST) {
      startOfPeriod.setDate(1);
      startOfPeriod.setHours(0, 0, 0, 0);
      endOfPeriod.setMonth(endOfPeriod.getMonth() + 1);
      endOfPeriod.setDate(0);
      endOfPeriod.setHours(23, 59, 59, 999);
    } else if (view === VIEWS.WEEK) {
      const dayOfWeek = startOfPeriod.getDay();
      startOfPeriod.setDate(startOfPeriod.getDate() - dayOfWeek);
      startOfPeriod.setHours(0, 0, 0, 0);
      endOfPeriod.setDate(startOfPeriod.getDate() + 6);
      endOfPeriod.setHours(23, 59, 59, 999);
    } else if (view === VIEWS.DAY) {
      startOfPeriod.setHours(0, 0, 0, 0);
      endOfPeriod.setHours(23, 59, 59, 999);
    }
    
    filter.shiftDate = {
      greaterThanOrEqual: startOfPeriod.toISOString(),
      lessThanOrEqual: endOfPeriod.toISOString(),
    };
    
    console.log('Date range filter:', {
      start: startOfPeriod.toISOString(),
      end: endOfPeriod.toISOString(),
      view: view,
      currentDate: currentDate
    });
    
    console.log('Final API filter:', filter);
    return filter;
  }, [filters, currentDate, view, user, userCohortIds, canSeeAllInstitutions]);
  
  // Fetch users for student selection
  const [{ data: users }] = useFindMany(api.user, {
    filter: scopedInstitution ? { institution: { equals: scopedInstitution } } : {},
    select: {
      id: true,
      firstName: true,
      lastName: true,
      email: true,
      institutionRole: true,
      roles: { key: true, name: true },
      cohort: { id: true, name: true },
      institution: true,
    },
    first: 250,
  });

  // Fetch shifts
  const [{ data: shifts, fetching, error }, refetchShifts] = useFindMany(api.shift, {
    filter: apiFilter,
    sort: { shiftDate: 'Ascending' },
    select: {
      id: true,
      shiftDate: true,
      endTime: true,
      shiftType: true,
      status: true,
      institution: true,
      location: true,
      unit: true,
      department: true,
      syncSource: true,
      fisdapEventUid: true,
      fisdapUrl: true,
      fisdapInstructorName: true,
      lastSyncedAt: true,
      cohort: { id: true, name: true },
      locked: true,
      maxStudents: true,
      isOpenEnrollment: true,
      enrollmentPeriodStart: true,
      enrollmentPeriodEnd: true,
      requiresPreceptorApproval: true,
      agency: {
        id: true,
        name: true,
      },
      clinicalSite: {
        id: true,
        name: true,
        units: true,
      },
      preceptor: {
        id: true,
        firstName: true,
        lastName: true,
      },
    },
    first: 250,
  });

  const selectedCohortId = filters.cohort && filters.cohort !== 'all' ? filters.cohort : null;
  const currentShiftIds = useMemo(() => (shifts || []).map((shift) => shift.id).filter(Boolean), [shifts]);

  const shiftDocumentationFilter = useMemo(() => {
    if (currentShiftIds.length === 0) {
      return { shiftId: { equals: "__none__" } };
    }
    return { shiftId: { in: currentShiftIds } };
  }, [currentShiftIds]);

  const [{ data: shiftDocumentations, fetching: shiftDocumentationsFetching }] = useFindMany(api.shiftDocumentation, {
    filter: shiftDocumentationFilter,
    select: {
      id: true,
      enrollmentStatus: true,
      completionStatus: true,
      shift: { id: true },
      student: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
        institution: true,
      },
    },
    first: 250,
  });

  const shiftDocumentationsByShiftId = useMemo(() => {
    const byShift = new Map();
    (shiftDocumentations || []).forEach((doc) => {
      const shiftId = doc.shift?.id;
      if (!shiftId) return;
      const docs = byShift.get(shiftId) || [];
      docs.push(doc);
      byShift.set(shiftId, docs);
    });
    return byShift;
  }, [shiftDocumentations]);

  const studentOptions = useMemo(() => {
    const byId = new Map();

    (users || []).forEach((candidate) => {
      if (!isStudent(candidate)) return;
      if (scopedInstitution && candidate.institution !== scopedInstitution) return;
      byId.set(candidate.id, candidate);
    });

    (shiftDocumentations || []).forEach((doc) => {
      if (!doc.student?.id) return;
      if (scopedInstitution && doc.student.institution !== scopedInstitution) return;
      byId.set(doc.student.id, doc.student);
    });

    return Array.from(byId.values()).sort((a, b) => {
      const aName = getStudentDisplayName(a).toLowerCase();
      const bName = getStudentDisplayName(b).toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [users, shiftDocumentations, scopedInstitution]);

  const displayShifts = useMemo(() => {
    if (!filters.student) return shifts || [];
    return (shifts || []).filter((shift) =>
      (shiftDocumentationsByShiftId.get(shift.id) || []).some((doc) => doc.student?.id === filters.student)
    );
  }, [shifts, filters.student, shiftDocumentationsByShiftId]);

  // Debug shifts data
  console.log('=== DEBUG: Shifts Data ===');
  console.log('Shifts fetching:', fetching);
  console.log('Shifts error:', error);
  console.log('Shifts count:', shifts?.length || 0);
  console.log('Sample shifts:', shifts?.slice(0, 3));
  console.log('========================');

  // Fetch agencies
  const [{ data: agencies, fetching: agenciesFetching }] = useFindMany(api.agency, {
    select: {
      id: true,
      name: true,
      institutionalAffiliations: true,
      sharedWithInstitutions: true,
      originalInstitution: true,
    },
    first: 250,
  });

  // Fetch all clinical sites with their agency relationships
  const [{ data: clinicalSites, fetching: clinicalSitesFetching, error: clinicalSitesError }] = useFindMany(api.clinicalSite, {
    select: {
      id: true,
      name: true,
      units: true,
      agency: {
        id: true,
        name: true,
        institutionalAffiliations: true,
        sharedWithInstitutions: true,
        originalInstitution: true,
      },
    },
    first: 250,
  });

  // Use the same data for both contexts since we're fetching all clinical sites with agency info
  const allClinicalSites = clinicalSites;
  const allClinicalSitesFetching = clinicalSitesFetching;

  // Fetch all users to extract institution data
  const [{ data: allUsers, fetching: allUsersFetching }] = useFindMany(api.user, {
    select: {
      id: true,
      institution: true,
    },
    first: 250,
  });

  // Shift actions
  const [{ fetching: claimFetching }, claimShift] = useAction(api.shift.update);
  const [{ fetching: createFetching }, createShift] = useAction(api.shift.create);
  const [{ fetching: deleteFetching }, deleteShift] = useAction(api.shift.delete);
  const [{ fetching: syncFetching }, syncCalendarAction] = useAction(api.syncFisdapCalendar);
  
  // Email sending — uses fetch to avoid SDK crash if action not yet in generated client
  const [emailFetching, setEmailFetching] = useState(false);
  const sendEmail = useCallback(async (params) => {
    setEmailFetching(true);
    try {
      if (api.testActualSendMail) {
        await api.testActualSendMail(params);
      } else {
        const res = await fetch("/api/send-calendar-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(params),
        });
        if (!res.ok) throw new Error("Failed to send email");
      }
    } finally {
      setEmailFetching(false);
    }
  }, []);

  // Helper function to get available agencies - moved here before useEffects
  const getAvailableAgencies = useCallback((institutionOverride = null) => {
    const targetInstitution = institutionOverride || filters.institution;
    console.log('=== DEBUG: getAvailableAgencies ===');
    console.log('agencies:', agencies);
    console.log('agenciesFetching:', agenciesFetching);
    console.log('targetInstitution:', targetInstitution);
    console.log('filters.institution:', filters.institution);
    console.log('institutionOverride:', institutionOverride);
    
    if (!agencies || agenciesFetching) {
      console.log('No agencies or still fetching');
      return [];
    }
    
    const filtered = agencies.filter(agency => {
      if (!targetInstitution) {
        console.log(`No target institution, including agency ${agency.name}`);
        return true;
      }

      const matches = recordMatchesInstitution(agency, targetInstitution);
      console.log(`Agency ${agency.name} matches '${targetInstitution}': ${matches}`);
      return matches;
    });
    
    console.log('Filtered agencies count:', filtered.length);
    console.log('Filtered agencies:', filtered);
    return filtered;
  }, [agencies, agenciesFetching, filters.institution]);

  // Helper function to get available clinical sites - moved here before useEffects
  const getAvailableClinicalSites = useCallback((institutionOverride = null) => {
    const targetInstitution = institutionOverride || filters.institution;
    console.log('=== DEBUG: getAvailableClinicalSites ===');
    console.log('targetInstitution:', targetInstitution);
    console.log('institutionOverride:', institutionOverride);
    console.log('filters.institution:', filters.institution);
    
    const sitesToUse = clinicalSites;
    const fetchingStatus = clinicalSitesFetching;
    
    console.log('sitesToUse:', sitesToUse);
    console.log('fetchingStatus:', fetchingStatus);
    console.log('clinicalSitesError:', clinicalSitesError);
    
    if (!sitesToUse || fetchingStatus) {
      console.log('No clinical sites or still fetching');
      return [];
    }
    
    if (!targetInstitution) {
      console.log('No target institution, returning all sites');
      return sitesToUse;
    }
    
    const filtered = sitesToUse.filter(site => {
      if (!site.agency) {
        console.log(`Clinical site ${site.name} has no agency, excluding`);
        return false;
      }

      const matches = recordMatchesInstitution(site.agency, targetInstitution);
      console.log(`Clinical site ${site.name} agency matches '${targetInstitution}': ${matches}`);
      return matches;
    });
    
    console.log('Filtered clinical sites result:', filtered);
    return filtered;
  }, [clinicalSites, clinicalSitesFetching, filters.institution, clinicalSitesError]);

  // Initialize institution in newShiftData when modal opens
  useEffect(() => {
    if (isAddShiftOpen) {
      console.log('=== DEBUG: Add Shift Modal Opened - Institution Initialization ===');
      
      // Determine the institution to use
      const targetInstitution = filters.institution || user?.institution || '';
      
      console.log('Institution resolution:');
      console.log('- filters.institution:', filters.institution);
      console.log('- user.institution:', user?.institution);
      console.log('- target institution:', targetInstitution);
      console.log('- current newShiftData.institution:', newShiftData.institution);
      
      // Only update if the institution is different or empty
      if (!newShiftData.institution || newShiftData.institution !== targetInstitution) {
        console.log('Updating newShiftData.institution from', newShiftData.institution, 'to', targetInstitution);
        
        setNewShiftData(prev => {
          const updated = {
            ...prev,
            institution: targetInstitution,
            // Clear dependent fields when institution changes
            agencyId: prev.institution !== targetInstitution ? '' : prev.agencyId,
            clinicalSiteId: prev.institution !== targetInstitution ? '' : prev.clinicalSiteId,
          };
          
          console.log('newShiftData updated:', updated);
          return updated;
        });
      } else {
        console.log('Institution already set correctly, no update needed');
      }
    }
  }, [isAddShiftOpen, filters.institution, user?.institution, newShiftData.institution]);

  // Debug logging when modal opens - moved after all state declarations
  useEffect(() => {
    if (isAddShiftOpen) {
      console.log('=== DEBUG: Add Shift Modal State Analysis ===');
      console.log('Current newShiftData:', newShiftData);
      console.log('Current filters:', filters);
      console.log('User institution:', user?.institution);
      console.log('Agencies data:', agencies);
      console.log('Agencies fetching:', agenciesFetching);
      console.log('Clinical sites data:', clinicalSites);
      console.log('All clinical sites data:', allClinicalSites);
      console.log('Clinical sites fetching:', clinicalSitesFetching);
      console.log('All clinical sites fetching:', allClinicalSitesFetching);
      
      // Test getAvailableAgencies immediately
      const testAgencies = getAvailableAgencies(newShiftData.institution);
      console.log('getAvailableAgencies result:', testAgencies);
      console.log('getAvailableAgencies count:', testAgencies.length);
      
      // Test getAvailableClinicalSites immediately
      const testSites = getAvailableClinicalSites(newShiftData.institution);
      console.log('getAvailableClinicalSites result:', testSites);
      console.log('getAvailableClinicalSites count:', testSites.length);
    }
  }, [isAddShiftOpen, newShiftData, filters, user, agencies, agenciesFetching, clinicalSites, allClinicalSites, clinicalSitesFetching, allClinicalSitesFetching, getAvailableAgencies, getAvailableClinicalSites]);

  // Claim shift function
  const handleClaimShift = useCallback(async (shift) => {
    if (!user || !isStudent(user)) {
      toast({
        title: 'Access Denied',
        description: 'Only students can claim shifts.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      // Use shift enrollment system instead of direct student assignment
      await claimShift({
        id: shift.id,
        // Note: This may need to be updated based on actual enrollment system
        // For now, we'll try to update the shift status or use a different approach
        status: 'in_progress',
      });
      
      toast({
        title: 'Shift Claimed',
        description: 'You have successfully claimed this shift.',
        status: 'success',
        duration: 3000,
      });
      
      await refetchShifts();
    } catch (error) {
      toast({
        title: 'Error Claiming Shift',
        description: error.message || 'Failed to claim shift.',
        status: 'error',
        duration: 3000,
      });
    }
  }, [user, claimShift, toast, refetchShifts]);

  const handleManualSync = useCallback(async ({ clearExisting = false } = {}) => {
    if (!selectedCohortId && (!canSeeAllInstitutions || clearExisting)) {
      toast({
        title: 'Select a cohort first',
        description: clearExisting
          ? 'Pick a specific cohort before resetting synced shifts.'
          : 'Pick a specific cohort in the calendar filters before refreshing the sync.',
        status: 'warning',
        duration: 4000,
      });
      return;
    }

    try {
      const payload = {
        forceSync: true,
      };
      if (selectedCohortId) payload.cohortId = selectedCohortId;
      if (clearExisting) payload.clearExisting = true;
      if (user?.id) payload.initiatedByUserId = user.id;

      const response = await syncCalendarAction(payload);
      const result = selectedCohortId
        ? response?.results?.find((r) => r.cohortId === selectedCohortId) || response?.results?.[0]
        : null;
      const allResults = response?.results || [];
      const pieces = [];
      if (result?.created) pieces.push(`${result.created} created`);
      if (result?.updated) pieces.push(`${result.updated} updated`);
      if (clearExisting && typeof result?.cleared === 'number') pieces.push(`${result.cleared} cleared`);
      if (!result && allResults.length > 0) {
        const totals = allResults.reduce((acc, entry) => ({
          created: acc.created + (entry.created || 0),
          updated: acc.updated + (entry.updated || 0),
          skipped: acc.skipped + (entry.skipped || 0),
        }), { created: 0, updated: 0, skipped: 0 });
        pieces.push(`${allResults.length} cohorts`);
        pieces.push(`${totals.created} created`);
        pieces.push(`${totals.updated} updated`);
        if (totals.skipped) pieces.push(`${totals.skipped} skipped`);
      }
      toast({
        title: 'Calendar refresh complete',
        description: pieces.length ? pieces.join(", ") : "Calendar sync finished",
        status: 'success',
        duration: 4000,
      });
      await refetchShifts();
    } catch (error) {
      toast({
        title: 'Calendar refresh failed',
        description: error?.message || "Unable to refresh the calendar link.",
        status: 'error',
        duration: 5000,
      });
    }
  }, [selectedCohortId, canSeeAllInstitutions, syncCalendarAction, user?.id, toast, refetchShifts]);

  // Quick add shift function
  const handleQuickAddShift = useCallback((date, shiftType) => {
    console.log('=== DEBUG: Quick Add Shift ===');
    
    // Check if institution is selected
    const currentInstitution = filters.institution || user?.institution || '';
    
    console.log('Quick add institution resolution:');
    console.log('- filters.institution:', filters.institution);
    console.log('- user.institution:', user?.institution);
    console.log('- current institution:', currentInstitution);
    
    if (!currentInstitution) {
      console.log('No institution available, showing warning');
      toast({
        title: 'Institution Required',
        description: 'Please select an institution before creating a shift.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    
    // Format date for datetime-local input (YYYY-MM-DDTHH:MM)
    const formattedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 8, 0)
      .toISOString().slice(0, 16);
    
    console.log('Setting newShiftData for quick add:');
    console.log('- formattedDate:', formattedDate);
    console.log('- shiftType:', shiftType);
    console.log('- institution:', currentInstitution);
    
    setNewShiftData(prev => {
      const updated = {
        ...prev,
        shiftDate: formattedDate,
        shiftType: shiftType,
        institution: currentInstitution,
        // Clear dependent fields to ensure fresh loading
        agencyId: '',
        clinicalSiteId: '',
        cohort: filters.cohort || userCohortIds[0] || "",
      };
      
      console.log('Quick add newShiftData updated:', updated);
      return updated;
    });
    
    onAddShiftOpen();
  }, [onAddShiftOpen, userCohortIds, filters.institution, filters.cohort, toast]);

  // Create shift function
  const handleCreateShift = useCallback(async () => {
    if (!user || !canManageCalendar) {
      toast({
        title: 'Access Denied',
        description: 'Only schedulers, instructors, and admins can create shifts.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Use the calendar's institution filter
    const currentInstitution = filters.institution || user?.institution || '';
    
    if (!newShiftData.agencyId || !newShiftData.shiftDate || !newShiftData.cohort || !currentInstitution || !newShiftData.location) {
      toast({
        title: 'Missing Required Fields',
        description: 'Please fill in all required fields. Institution must be selected in the calendar.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Validate enrollment periods
    if (newShiftData.enrollmentPeriodStart && newShiftData.enrollmentPeriodEnd) {
      const startDate = new Date(newShiftData.enrollmentPeriodStart);
      const endDate = new Date(newShiftData.enrollmentPeriodEnd);
      if (startDate >= endDate) {
        toast({
          title: 'Invalid Enrollment Period',
          description: 'Enrollment start date must be before end date.',
          status: 'error',
          duration: 3000,
        });
        return;
      }
    }

    try {
      // Determine student assignment
      let studentAssignment = null;
      if (newShiftData.addStudent && newShiftData.selectedStudents.length > 0) {
        // For now, assign first selected student (could be enhanced for multiple students)
        studentAssignment = { _link: newShiftData.selectedStudents[0] };
      }

      const shiftData = {
        agency: { _link: newShiftData.agencyId },
        shiftDate: formatDateTimeForAPI(newShiftData.shiftDate),
        endTime: formatDateTimeForAPI(newShiftData.endTime),
        shiftType: newShiftData.shiftType,
        cohort: newShiftData.cohort,
        institution: currentInstitution, // Use the calendar's institution
        location: newShiftData.location,
        unit: newShiftData.unit,
        maxStudents: parseInt(newShiftData.maxStudents),
        requiresPreceptorApproval: newShiftData.requiresApproval,
        enrollmentPeriodStart: formatDateTimeForAPI(newShiftData.enrollmentPeriodStart),
        enrollmentPeriodEnd: formatDateTimeForAPI(newShiftData.enrollmentPeriodEnd),
        status: newShiftData.requiresApproval ? 'scheduled' : 'scheduled', // Could be 'pending' if approval workflow
      };

      // Add clinical site if provided
      if (newShiftData.clinicalSiteId) {
        shiftData.clinicalSite = { _link: newShiftData.clinicalSiteId };
      }

      // Note: Student assignment through enrollment system would be handled separately
      // Removing direct student assignment as it's not supported in the data model

      await createShift(shiftData);
      
      toast({
        title: 'Shift Created',
        description: 'New shift has been successfully created.',
        status: 'success',
        duration: 3000,
      });
      
      // Reset form and close modal
      setNewShiftData({
        agencyId: '',
        clinicalSiteId: '',
        shiftDate: '',
        endTime: '',
        shiftType: 'field',
        cohort: '',
        institution: currentInstitution, // Keep the current institution
        location: '',
        unit: '',
        maxStudents: 1,
        enrollmentPeriodStart: '',
        enrollmentPeriodEnd: '',
        addStudent: false,
        selectedStudents: [],
        requiresApproval: false,
      });
      onAddShiftClose();
      await refetchShifts();
    } catch (error) {
      toast({
        title: 'Error Creating Shift',
        description: error.message || 'Failed to create shift.',
        status: 'error',
        duration: 3000,
      });
    }
  }, [user, canManageCalendar, createShift, newShiftData, toast, onAddShiftClose, refetchShifts, filters.institution]);

  // Delete shift function with confirmation
  const handleDeleteShift = useCallback(async (shiftId) => {
    if (!user || !hasAnyRole(user?.roles, ['admin', 'system-admin'])) {
      toast({
        title: 'Access Denied',
        description: 'Only admins can delete shifts.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to delete this shift?\n\n' +
      '⚠️  This will remove all students assigned to this shift.\n' +
      '📋 Shift records will be archived but excluded from audits and reports.\n\n' +
      'This action cannot be undone.'
    );
    
    if (!confirmed) {
      return;
    }

    try {
      await deleteShift({ id: shiftId });
      
      toast({
        title: 'Shift Deleted',
        description: 'The shift has been successfully deleted and archived.',
        status: 'success',
        duration: 3000,
      });
      
      onShiftDetailClose();
      // Force refetch to ensure deleted shifts are removed from display
      await refetchShifts();
    } catch (error) {
      toast({
        title: 'Error Deleting Shift',
        description: error.message || 'Failed to delete shift.',
        status: 'error',
        duration: 3000,
      });
    }
  }, [user, deleteShift, toast, onShiftDetailClose, refetchShifts]);

  // Navigation functions
  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const navigateCalendar = useCallback((direction) => {
    const newDate = new Date(currentDate);
    
    if (view === VIEWS.MONTH || view === VIEWS.LIST) {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (view === VIEWS.WEEK) {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (view === VIEWS.DAY) {
      newDate.setDate(newDate.getDate() + direction);
    }
    
    setCurrentDate(newDate);
  }, [currentDate, view]);

  // Quick filter function
  const applyQuickFilter = useCallback((filterType) => {
    const now = new Date();
    let newFilters = { ...filters, quickFilter: filterType };
    
    if (filterType === QUICK_FILTERS.MY_SHIFTS) {
      // Reset other filters and rely on role-based filtering in apiFilter
      newFilters = {
        institution: '',
        cohort: '',
        shiftType: '',
        status: '',
        agency: '',
        clinicalSite: '',
        student: '',
        source: '',
        quickFilter: filterType,
      };
    } else if (filterType === QUICK_FILTERS.THIS_WEEK) {
      // Set view to week and navigate to current week
      setView(VIEWS.WEEK);
      setCurrentDate(new Date());
      newFilters.quickFilter = filterType;
    } else if (filterType === QUICK_FILTERS.THIS_MONTH) {
      // Set view to month and navigate to current month
      setView(VIEWS.MONTH);
      setCurrentDate(new Date());
      newFilters.quickFilter = filterType;
    } else if (filterType === QUICK_FILTERS.FISDAP) {
      setView(VIEWS.LIST);
      newFilters.source = "fisdap_ical";
      newFilters.quickFilter = filterType;
    }
    
    setFilters(newFilters);
  }, [filters]);

  // Handle shift click
  const handleShiftClick = useCallback((shift) => {
    setSelectedShift(shift);
    onShiftDetailOpen();
  }, [onShiftDetailOpen]);

  // Helper function to get unique institutions from all users
  const getUniqueInstitutions = useCallback(() => {
    const institutions = [
      ...(allUsers || []).map(user => user.institution),
      ...(cohorts || []).map(cohort => cohort.institution),
    ]
      .filter(institution => institution && institution.trim() !== '')
      .filter((institution, index, array) => array.indexOf(institution) === index)
      .sort();
    
    return institutions;
  }, [allUsers, cohorts]);

  // Helper function to get institutions user has access to
  const getAvailableInstitutions = useCallback(() => {
    if (!user) return [];
    
    if (canSeeAllInstitutions) {
      // Admins can see all institutions
      return getUniqueInstitutions();
    } else if (user.institution) {
      // Regular users see only their institution
      return [user.institution];
    }
    
    return [];
  }, [user, getUniqueInstitutions, canSeeAllInstitutions]);

  // Helper function to get shift display name
  const getShiftDisplayName = useCallback((shift) => {
    if (shift.agency && shift.clinicalSite) {
      return `${shift.agency.name} - ${shift.clinicalSite.name}`;
    } else if (shift.agency) {
      return shift.agency.name;
    } else if (shift.clinicalSite) {
      return shift.clinicalSite.name;
    }
    return 'Unknown Location';
  }, []);

  const getShiftStudentDocs = useCallback((shift) => {
    return shiftDocumentationsByShiftId.get(shift.id) || [];
  }, [shiftDocumentationsByShiftId]);

  // Helper function to get cohorts user has access to
  const getAvailableCohorts = useCallback(() => {
    if (!user) return [];

    const normalizedCohorts = (cohorts || []).map((cohort) => ({
      id: String(cohort.id),
      label: cohort.displayName || cohort.name || `Cohort ${cohort.id}`,
    }));

    if (canSeeAllInstitutions || isInstitutionAdministrator(user) || isScheduler(user)) {
      return [{ id: "all", label: "All Cohorts" }, ...normalizedCohorts];
    }

    const allowedIds = new Set(userCohortIds.map(String));
    const scoped = normalizedCohorts.filter((cohort) => allowedIds.has(cohort.id));

    if (isInstructor(user)) {
      return scoped.length > 0 ? [{ id: "all", label: "All Cohorts" }, ...scoped] : [];
    }

    if (isStudent(user)) {
      return scoped;
    }

    return [];
  }, [user, cohorts, userCohortIds, canSeeAllInstitutions]);

  // Check if shift can be claimed
  const canClaimShift = useCallback((shift) => {
    if (!user || !isStudent(user)) return false;
    if (!shift.isOpenEnrollment) return false;
    if (shift.locked) return false;
    if (shift.status !== 'scheduled') return false;
    
    // Check enrollment period
    const now = new Date();
    if (shift.enrollmentPeriodStart && new Date(shift.enrollmentPeriodStart) > now) return false;
    if (shift.enrollmentPeriodEnd && new Date(shift.enrollmentPeriodEnd) < now) return false;
    
    // Check if user is in the right cohort
    if (user.cohort?.name !== shift.cohort?.name) return false;
    
    return true;
  }, [user]);

  // Check if user can access shift details
  const canAccessShiftDetails = useCallback(() => {
    if (!user) return false;
    return canSeeAllInstitutions || isInstitutionAdministrator(user) || isScheduler(user) || isInstructor(user);
  }, [user, canSeeAllInstitutions]);

  // Navigate to shift details
  const handleNameClick = useCallback((shiftId) => {
    if (canAccessShiftDetails()) {
      navigate(`/shift-details/${shiftId}`);
    }
  }, [navigate, canAccessShiftDetails]);
  
  // Helper function to format date header

  // Helper function to detect email domain and map to calendar provider
  const detectCalendarProvider = useCallback((email) => {
    if (!email) return 'ical';
    
    const domain = email.toLowerCase().split('@')[1];
    
    const providerMap = {
      'gmail.com': 'google',
      'googlemail.com': 'google',
      'outlook.com': 'outlook',
      'hotmail.com': 'outlook',
      'live.com': 'outlook',
      'yahoo.com': 'yahoo',
      'yahoo.ca': 'yahoo',
      'yahoo.co.uk': 'yahoo',
    };
    
    return providerMap[domain] || 'ical';
  }, []);

  // Helper function to generate calendar link based on provider
  const generateCalendarLink = useCallback((provider) => {
    const baseUrls = {
      google: 'https://calendar.google.com/calendar/render',
      outlook: 'https://outlook.live.com/calendar/0/deeplink/compose',
      yahoo: 'https://calendar.yahoo.com/',
      ical: null // Will generate download link
    };
    
    return baseUrls[provider];
  }, []);

  // Helper function to generate email content
  const generateEmailContent = useCallback((calendarLink, provider, userEmail) => {
    const shiftsCount = displayShifts?.length || 0;
    const dateRange = view === VIEWS.MONTH 
      ? formatDateHeader()
      : view === VIEWS.WEEK 
        ? formatDateHeader()
        : currentDate.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          });
    
    const providerNames = {
      google: 'Google Calendar',
      outlook: 'Outlook Calendar',
      yahoo: 'Yahoo Calendar',
      ical: 'your calendar app'
    };
    
    const subject = `Your ElevationEMS Shift Calendar - ${dateRange}`;
    
    let content = `Hello,\n\n`;
    content += `Here is your personalized shift calendar for ${dateRange}.\n\n`;
    content += `📅 **Calendar Summary:**\n`;
    content += `• ${shiftsCount} shift${shiftsCount !== 1 ? 's' : ''} scheduled\n`;
    content += `• Date range: ${dateRange}\n`;
    content += `• Filtered for your access level\n\n`;
    
    if (provider === 'ical') {
      content += `📎 **Add to Calendar:**\n`;
      content += `An iCal file (.ics) has been attached to this email. You can:\n`;
      content += `• Double-click the attachment to add all shifts to your calendar\n`;
      content += `• Import the file into any calendar application\n`;
      content += `• Sync with your mobile device\n\n`;
    } else {
      content += `🔗 **Add to ${providerNames[provider]}:**\n`;
      content += `Click the link below to add these shifts to your ${providerNames[provider]}:\n`;
      content += `${calendarLink}\n\n`;
      content += `💡 **Tip:** You can also download the iCal file from the ElevationEMS calendar interface for backup.\n\n`;
    }
    
    content += `⚠️ **Important Notes:**\n`;
    content += `• This calendar reflects your current access permissions\n`;
    content += `• Shifts may be updated or cancelled - check the platform regularly\n`;
    content += `• Contact your administrator if you need access to additional shifts\n\n`;
    
    content += `Best regards,\n`;
    content += `ElevationEMS Team\n`;
    content += `support@elevationems.com`;
    
    return {
      subject,
      content,
      userEmail
    };
  }, [displayShifts, view, formatDateHeader, currentDate]);

  // Send calendar to email function
  const sendCalendarToEmail = useCallback(async () => {
    if (!user || !user.email) {
      toast({
        title: 'Email Not Available',
        description: 'Your email address is not available. Please contact support.',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    if (!displayShifts || displayShifts.length === 0) {
      toast({
        title: 'No Shifts to Send',
        description: 'There are no shifts in the current view to send via email.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      const provider = detectCalendarProvider(user.email);
      const calendarLink = generateCalendarLink(provider);
      const emailContent = generateEmailContent(calendarLink, provider, user.email);
      
      // Generate iCal content for attachment
      let icalContent = 'BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//ElevationEMS//Calendar//EN\n';
      
      displayShifts.forEach(shift => {
        const startDate = new Date(shift.shiftDate);
        const endDate = shift.endTime ? new Date(shift.endTime) : new Date(startDate.getTime() + 8 * 60 * 60 * 1000);
        
        const formatDateForICal = (date) => {
          return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
        };
        
        icalContent += 'BEGIN:VEVENT\n';
        icalContent += `UID:${shift.id}@elevationems.com\n`;
        icalContent += `DTSTART:${formatDateForICal(startDate)}\n`;
        icalContent += `DTEND:${formatDateForICal(endDate)}\n`;
        icalContent += `SUMMARY:${shift.shiftType.toUpperCase()} - ${getShiftDisplayName(shift)}\n`;
        icalContent += `DESCRIPTION:Location: ${shift.location || 'N/A'}\\nUnit: ${shift.unit || 'N/A'}\\nInstitution: ${shift.institution}\\nCohort: ${shift.cohort?.name || 'N/A'}\\nStatus: ${shift.status}\n`;
        icalContent += `LOCATION:${shift.location || getShiftDisplayName(shift)}\n`;
        icalContent += `STATUS:${shift.status === 'scheduled' ? 'CONFIRMED' : 'TENTATIVE'}\n`;
        icalContent += `CATEGORIES:${shift.shiftType},${shift.cohort?.name || 'unknown'}\n`;
        icalContent += 'END:VEVENT\n';
      });
      
      icalContent += 'END:VCALENDAR';

      // Send email with calendar data
      await sendEmail({
        to: user.email,
        subject: emailContent.subject,
        content: emailContent.content,
        attachments: [{
          filename: 'shifts.ics',
          content: icalContent,
          contentType: 'text/calendar'
        }]
      });

      toast({
        title: 'Calendar Email Sent',
        description: `Your shift calendar has been sent to ${user.email}. Check your email for the calendar link${provider === 'ical' ? ' and iCal attachment' : ''}.`,
        status: 'success',
        duration: 5000,
      });

    } catch (error) {
      console.error('Error sending calendar email:', error);
      toast({
        title: 'Failed to Send Email',
        description: error.message || 'There was an error sending your calendar email. Please try again or contact support.',
        status: 'error',
        duration: 5000,
      });
    }
  }, [user, displayShifts, detectCalendarProvider, generateCalendarLink, generateEmailContent, sendEmail, toast]);

  // Export functions
  const exportToGoogleCalendar = useCallback(() => {
    const googleUrl = 'https://calendar.google.com/calendar/render';
    toast({
      title: 'Redirecting to Google Calendar',
      description: 'You will be redirected to add events to your Google Calendar.',
      status: 'info',
      duration: 3000,
    });
    window.open(googleUrl, '_blank');
  }, [toast]);
  
  const exportToOutlook = useCallback(() => {
    const outlookUrl = 'https://outlook.live.com/calendar/0/deeplink/compose';
    toast({
      title: 'Redirecting to Outlook',
      description: 'You will be redirected to add events to your Outlook Calendar.',
      status: 'info',
      duration: 3000,
    });
    window.open(outlookUrl, '_blank');
  }, [toast]);
  
  const downloadICal = useCallback(() => {
    if (!displayShifts || displayShifts.length === 0) return;
    
    let icalContent = 'BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//PapaMedic//Calendar//EN\n';
    
    displayShifts.forEach(shift => {
      const startDate = new Date(shift.shiftDate);
      const endDate = shift.endTime ? new Date(shift.endTime) : new Date(startDate.getTime() + 8 * 60 * 60 * 1000);
      
      const formatDateForICal = (date) => {
        return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      };
      
      icalContent += 'BEGIN:VEVENT\n';
      icalContent += `UID:${shift.id}@papamedic.gadget.app\n`;
      icalContent += `DTSTART:${formatDateForICal(startDate)}\n`;
      icalContent += `DTEND:${formatDateForICal(endDate)}\n`;
      icalContent += `SUMMARY:${shift.shiftType} - ${getShiftDisplayName(shift)}\n`;
      icalContent += `DESCRIPTION:Location: ${shift.location || 'N/A'}\\nUnit: ${shift.unit || 'N/A'}\\nInstitution: ${shift.institution}\\nCohort: ${shift.cohort?.name || 'N/A'}\n`;
      icalContent += `LOCATION:${shift.location || getShiftDisplayName(shift)}\n`;
      icalContent += 'END:VEVENT\n';
    });
    
    icalContent += 'END:VCALENDAR';
    
    const blob = new Blob([icalContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'shifts.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Calendar Downloaded',
      description: 'Your shifts have been downloaded as an iCal file.',
      status: 'success',
      duration: 3000,
    });
  }, [displayShifts, toast]);
  
  // Render calendar grid
  const renderCalendarGrid = () => {
    if (!displayShifts) return null;
    
    if (view === VIEWS.MONTH) {
      return renderMonthView();
    } else if (view === VIEWS.WEEK) {
      return renderWeekView();
    } else if (view === VIEWS.DAY) {
      return renderDayView();
    }

    return renderListView();
  };
  
  const renderMonthView = () => {
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());
    
    const days = [];
    const currentDateObj = new Date(startDate);
    
    // Generate 6 weeks (42 days)
    for (let i = 0; i < 42; i++) {
      // Create a proper date copy for this cell
      const cellDate = new Date(currentDateObj);
      
      // Apply client-side filtering for agency and clinical site
      let dayShifts = displayShifts?.filter(shift => {
        const shiftDate = new Date(shift.shiftDate);
        const dateMatch = (
          shiftDate.getDate() === currentDateObj.getDate() &&
          shiftDate.getMonth() === currentDateObj.getMonth() &&
          shiftDate.getFullYear() === currentDateObj.getFullYear()
        );
              
        if (!dateMatch) return false;
              
        // Client-side agency filtering
        if (filters.agency && shift.agency?.id !== filters.agency) {
          return false;
        }
              
        // Client-side clinical site filtering
        if (filters.clinicalSite && shift.clinicalSite?.id !== filters.clinicalSite) {
          return false;
        }
              
        return true;
      }) || [];
      
      const isCurrentMonth = currentDateObj.getMonth() === currentDate.getMonth();
      const isToday = 
        currentDateObj.getDate() === new Date().getDate() &&
        currentDateObj.getMonth() === new Date().getMonth() &&
        currentDateObj.getFullYear() === new Date().getFullYear();
      
      // Auto-scale based on number of shifts
      const shiftCount = dayShifts.length;
      let maxVisible = 6;
      let shiftHeight = "16px";
      let fontSize = "10px";
      let spacing = 0.5;
      let paddingX = "3px";
      let paddingY = "1px";
      
      // Adjust based on shift count
      if (shiftCount > 8) {
        maxVisible = 10;
        shiftHeight = "14px";
        fontSize = "9px";
        spacing = 0.25;
        paddingX = "2px";
        paddingY = "1px";
      } else if (shiftCount > 6) {
        maxVisible = 8;
        shiftHeight = "15px";
        fontSize = "9px";
        spacing = 0.25;
        paddingX = "2px";
        paddingY = "1px";
      } else if (shiftCount > 4) {
        maxVisible = 6;
        shiftHeight = "16px";
        fontSize = "10px";
        spacing = 0.5;
        paddingX = "3px";
        paddingY = "1px";
      }
      
      days.push(
        <GridItem
          key={i}
          border="1px"
          borderColor={borderColor}
          minH="80px"
          p={0.5}
          bg="white"
          position="relative"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Text
            fontSize="sm"
            fontWeight={isToday ? 'bold' : 'semibold'}
            color={isToday ? 'blue.500' : isCurrentMonth ? 'gray.800' : 'gray.400'}
            mb={0.5}
            px={1}
            lineHeight="1"
            flexShrink={0}
          >
            {currentDateObj.getDate()}
          </Text>
          <VStack spacing={spacing} align="stretch" px={0.5} flex={1} overflow="hidden">
            {dayShifts.slice(0, maxVisible).map(shift => {
              // Get shift type emoji
              const shiftTypeEmojis = {
                field: '🚑',
                hospital: '🏥', 
                lab: '🧪',
                field_internship: '🏁',
              };
              
              // Get display name and truncate if needed
              const displayName = getShiftDisplayName(shift);
              const locationText = displayName.length > 12 
                ? displayName.substring(0, 10) + '..'
                : displayName;
              
              return (
                <Box
                  key={shift.id}
                  bg={`${SHIFT_TYPE_COLORS[shift.shiftType] || 'gray'}.100`}
                  borderLeft="2px solid"
                  borderLeftColor={`${STATUS_COLORS[shift.status] || 'gray'}.500`}
                  p={`${paddingY} ${paddingX}`}
                  borderRadius="2px"
                  cursor="pointer"
                  fontSize={fontSize}
                  h={shiftHeight}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  _hover={{ bg: `${SHIFT_TYPE_COLORS[shift.shiftType] || 'gray'}.200` }}
                  onClick={() => handleShiftClick(shift)}
                  overflow="hidden"
                >
                  <HStack spacing={1} flex={1} minW={0}>
                    <Text fontSize="8px" lineHeight="1" flexShrink={0}>
                      {shiftTypeEmojis[shift.shiftType]}
                    </Text>
                    <Text 
                      fontWeight="semibold" 
                      noOfLines={1} 
                      color="gray.800"
                      lineHeight="1"
                      flex={1}
                      minW={0}
                      fontSize={fontSize}
                    >
                      {locationText}
                    </Text>
                  </HStack>
                  {canClaimShift(shift) && (
                    <Box
                      w="4px"
                      h="4px"
                      bg="green.500"
                      borderRadius="50%"
                      flexShrink={0}
                      ml={1}
                      title="Available"
                    />
                  )}
                </Box>
              );
            })}
            {dayShifts.length > maxVisible && (
              <Text fontSize="8px" color="gray.500" px={1} lineHeight="1" textAlign="center" h="10px" display="flex" alignItems="center" justifyContent="center">
                +{dayShifts.length - maxVisible} more
              </Text>
            )}
          </VStack>
          
          {/* Quick Add Circles - only show for authorized users */}
          {isCurrentMonth && user && canManageCalendar && (
            <HStack 
              spacing={1} 
              justify="space-between" 
              mt="auto"
              mb={0.5}
              px={0.5}
              h="16px"
              flexShrink={0}
              w="100%"
            >
              {Object.entries(SHIFT_TYPE_COLORS).map(([shiftType, color]) => {
                const shiftTypeEmojis = {
                  field: '🚑',
                  hospital: '🏥', 
                  lab: '🧪',
                  field_internship: '🏁',
                };
                
                return (
                  <Box
                    key={shiftType}
                    position="relative"
                    cursor="pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickAddShift(cellDate, shiftType);
                    }}
                    title={`Add ${shiftType.replace('_', ' ')} shift`}
                    _hover={{
                      transform: 'scale(1.1)',
                    }}
                    _active={{
                      transform: 'scale(0.9)'
                    }}
                    flex={1}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    h="16px"
                    w="100%"
                  >
                    <Text
                      fontSize="14px"
                      lineHeight="1"
                      position="relative"
                      display="inline-block"
                      filter="brightness(0.9)"
                      _hover={{
                        filter: "brightness(1.1)"
                      }}
                    >
                      {shiftTypeEmojis[shiftType]}
                    </Text>
                    
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      color="blue.500"
                      fontSize="8px"
                      fontWeight="bold"
                      textShadow="0 0 2px rgba(255,255,255,0.8)"
                      pointerEvents="none"
                      zIndex={1}
                    >
                      +
                    </Box>
                  </Box>
                );
              })}
            </HStack>
          )}
        </GridItem>
      );
      
      currentDateObj.setDate(currentDateObj.getDate() + 1);
    }
    
    return (
      <Box>
        <Grid templateColumns="repeat(7, 1fr)" gap={0} border="1px" borderColor={borderColor} bg="white">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <GridItem key={day} p={2} bg={useColorModeValue('gray.100', 'gray.700')} textAlign="center">
              <Text fontWeight="semibold">{day}</Text>
            </GridItem>
          ))}
          {days}
        </Grid>
      </Box>
    );
  };
  
  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      weekDays.push(day);
    }
    
    return (
      <Grid templateColumns="repeat(7, 1fr)" gap={2}>
        {weekDays.map((day, index) => {
          // Apply client-side filtering for agency and clinical site
          let dayShifts = displayShifts?.filter(shift => {
            const shiftDate = new Date(shift.shiftDate);
            const dateMatch = (
              shiftDate.getDate() === day.getDate() &&
              shiftDate.getMonth() === day.getMonth() &&
              shiftDate.getFullYear() === day.getFullYear()
            );
            
            if (!dateMatch) return false;
            
            // Client-side agency filtering
            if (filters.agency && shift.agency?.id !== filters.agency) {
              return false;
            }
            
            // Client-side clinical site filtering
            if (filters.clinicalSite && shift.clinicalSite?.id !== filters.clinicalSite) {
              return false;
            }
            
            return true;
          }) || [];
          
          const isToday = 
            day.getDate() === new Date().getDate() &&
            day.getMonth() === new Date().getMonth() &&
            day.getFullYear() === new Date().getFullYear();
          
          return (
            <GridItem key={index}>
              <Card size="sm">
                <CardHeader pb={1} p={2}>
                  <Text
                    fontSize="md"
                    fontWeight="bold"
                    color={isToday ? 'blue.500' : 'inherit'}
                  >
                    {day.getDate()}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                  </Text>
                </CardHeader>
                <CardBody pt={0} p={2}>
                  <VStack spacing={1} align="stretch">
                    {dayShifts.map(shift => (
                      <Box
                        key={shift.id}
                        bg={`${SHIFT_TYPE_COLORS[shift.shiftType] || 'gray'}.100`}
                        borderLeft="2px solid"
                        borderLeftColor={`${STATUS_COLORS[shift.status] || 'gray'}.500`}
                        p={1}
                        borderRadius="sm"
                        cursor="pointer"
                        position="relative"
                        _hover={{ bg: `${SHIFT_TYPE_COLORS[shift.shiftType] || 'gray'}.200` }}
                        onClick={() => handleShiftClick(shift)}
                      >
                        <HStack justify="space-between" align="start">
                          <VStack align="start" spacing={0} flex={1}>
                            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                              {getShiftDisplayName(shift)}
                            </Text>
                            <Text fontSize="xs" color="gray.600" noOfLines={1}>
                              {new Date(shift.shiftDate).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: false
                              })}
                            </Text>
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                              {shift.shiftType}
                            </Text>
                          </VStack>
                          {canClaimShift(shift) && (
                            <Badge colorScheme="green" size="sm" fontSize="xs">
                              Open
                            </Badge>
                          )}
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          );
        })}
      </Grid>
    );
  };
  
  const renderDayView = () => {
    // Apply client-side filtering for agency and clinical site
    const dayShifts = displayShifts?.filter(shift => {
      const shiftDate = new Date(shift.shiftDate);
      const dateMatch = (
        shiftDate.getDate() === currentDate.getDate() &&
        shiftDate.getMonth() === currentDate.getMonth() &&
        shiftDate.getFullYear() === currentDate.getFullYear()
      );
      
      if (!dateMatch) return false;
      
      // Client-side agency filtering
      if (filters.agency && shift.agency?.id !== filters.agency) {
        return false;
      }
      
      // Client-side clinical site filtering
      if (filters.clinicalSite && shift.clinicalSite?.id !== filters.clinicalSite) {
        return false;
      }
      
      return true;
    }) || [];
    
    return (
      <Card>
        <CardHeader p={3}>
          <Heading size="md">
            {currentDate.toLocaleDateString('en-US', { 
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Heading>
        </CardHeader>
        <CardBody p={3}>
          {dayShifts.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={4}>
              No shifts scheduled for this day
            </Text>
          ) : (
            <VStack spacing={2} align="stretch">
              {dayShifts.map(shift => (
                <Box
                  key={shift.id}
                  bg={`${SHIFT_TYPE_COLORS[shift.shiftType] || 'gray'}.100`}
                  borderLeft="3px solid"
                  borderLeftColor={`${STATUS_COLORS[shift.status] || 'gray'}.500`}
                  p={3}
                  borderRadius="md"
                  cursor="pointer"
                  position="relative"
                  _hover={{ bg: `${SHIFT_TYPE_COLORS[shift.shiftType] || 'gray'}.200` }}
                  onClick={() => handleShiftClick(shift)}
                >
                  <HStack justify="space-between" mb={2} align="start">
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="md" fontWeight="bold">
                        {getShiftDisplayName(shift)}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {new Date(shift.shiftDate).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: false
                        })}
                        {shift.endTime && ` - ${new Date(shift.endTime).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: false
                        })}`}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {shift.shiftType} • {shift.clinicalSite?.units && Array.isArray(shift.clinicalSite.units) && shift.clinicalSite.units.length > 0 ? formatClinicalSiteUnit(shift.clinicalSite.units[0]) : 'N/A'}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {shift.institution} • {shift.cohort?.name || 'N/A'}
                      </Text>
                      {shift.preceptor && (
                        <Text fontSize="sm" color="purple.600">
                          Preceptor: {shift.preceptor.firstName} {shift.preceptor.lastName}
                        </Text>
                      )}
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Tag colorScheme={SHIFT_TYPE_COLORS[shift.shiftType] || 'gray'} size="sm">
                        {shift.shiftType}
                      </Tag>
                      {canClaimShift(shift) && (
                        <Button
                          colorScheme="green"
                          size="sm"
                          leftIcon={<AddIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClaimShift(shift);
                          }}
                          isLoading={claimFetching}
                        >
                          Claim
                        </Button>
                      )}
                      {shift.locked && (
                        <Tag colorScheme="red" size="sm">
                          512 Locked
                        </Tag>
                      )}
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}
        </CardBody>
      </Card>
    );
  };

  const renderListView = () => {
    const grouped = new Map();

    (displayShifts || []).forEach((shift) => {
      const dateKey = new Date(shift.shiftDate).toISOString().slice(0, 10);
      const group = grouped.get(dateKey) || [];
      group.push(shift);
      grouped.set(dateKey, group);
    });

    const groups = Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b));

    if (groups.length === 0) {
      return (
        <Card>
          <CardBody>
            <Text color="gray.500" textAlign="center" py={8}>
              No shifts match the current filters
            </Text>
          </CardBody>
        </Card>
      );
    }

    return (
      <VStack spacing={3} align="stretch">
        {groups.map(([dateKey, dayShifts]) => {
          const day = new Date(`${dateKey}T00:00:00`);
          return (
            <Box key={dateKey} bg="white" border="1px solid" borderColor={borderColor} borderRadius="md" overflow="hidden">
              <Flex align="center" justify="space-between" px={4} py={3} bg={sectionHeaderBg}>
                <Heading size="sm">
                  {day.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Heading>
                <Badge colorScheme="blue">{dayShifts.length} shifts</Badge>
              </Flex>
              <VStack spacing={0} align="stretch">
                {dayShifts.map((shift) => {
                  const studentDocs = getShiftStudentDocs(shift);
                  return (
                    <Flex
                      key={shift.id}
                      gap={4}
                      align="center"
                      px={4}
                      py={3}
                      borderTop="1px solid"
                      borderColor={borderColor}
                      cursor="pointer"
                      _hover={{ bg: hoverBg }}
                      onClick={() => handleShiftClick(shift)}
                    >
                      <Box w="92px" flexShrink={0}>
                        <Text fontWeight="semibold">
                          {new Date(shift.shiftDate).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </Text>
                        {shift.endTime && (
                          <Text fontSize="xs" color="gray.500">
                            to {new Date(shift.endTime).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </Text>
                        )}
                      </Box>

                      <Box flex={1} minW={0}>
                        <HStack spacing={2} mb={1} flexWrap="wrap">
                          <Text fontWeight="semibold" noOfLines={1}>
                            {getShiftDisplayName(shift)}
                          </Text>
                          <Badge colorScheme={SHIFT_TYPE_COLORS[shift.shiftType] || 'gray'}>
                            {shift.shiftType}
                          </Badge>
                          {shift.syncSource === "fisdap_ical" && (
                            <Badge colorScheme="cyan">FISDAP</Badge>
                          )}
                        </HStack>
                        <Text fontSize="sm" color="gray.600" noOfLines={1}>
                          {shift.institution} • {shift.cohort?.name || 'No cohort'} • {shift.unit || shift.department || 'No unit'}
                        </Text>
                        {shift.fisdapEventUid && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            FISDAP UID: {shift.fisdapEventUid}
                          </Text>
                        )}
                      </Box>

                      <Box w="230px" flexShrink={0}>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>
                          Students
                        </Text>
                        {shiftDocumentationsFetching ? (
                          <Spinner size="xs" />
                        ) : studentDocs.length > 0 ? (
                          <VStack align="stretch" spacing={1}>
                            {studentDocs.slice(0, 3).map((doc) => (
                              <Text key={doc.id} fontSize="sm" noOfLines={1}>
                                {getStudentDisplayName(doc.student)}
                              </Text>
                            ))}
                            {studentDocs.length > 3 && (
                              <Text fontSize="xs" color="gray.500">
                                +{studentDocs.length - 3} more
                              </Text>
                            )}
                          </VStack>
                        ) : (
                          <Text fontSize="sm" color="gray.500">No students</Text>
                        )}
                      </Box>

                      <Box w="100px" flexShrink={0} textAlign="right">
                        <Badge colorScheme={STATUS_COLORS[shift.status] || 'gray'}>{shift.status}</Badge>
                      </Box>
                    </Flex>
                  );
                })}
              </VStack>
            </Box>
          );
        })}
      </VStack>
    );
  };
  
  function formatDateHeader() {
    if (view === VIEWS.MONTH || view === VIEWS.LIST) {
      return currentDate.toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
      });
    } else if (view === VIEWS.WEEK) {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      return `${startOfWeek.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      })} - ${endOfWeek.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      })}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
  
  if (error) {
    return (
      <Box p={8} textAlign="center">
        <Text color="red.500" fontSize="lg">
          Error loading shifts: {error.toString()}
        </Text>
        <Button onClick={() => refetchShifts()} mt={4}>
          Retry
        </Button>
      </Box>
    );
  }
  
  return (
    <Box p={3}>
      {/* Header with Title and Main Controls */}
      <HStack justify="space-between" mb={3} spacing={4}>
        <HStack spacing={3}>
          <Heading size="lg">Shift Calendar</Heading>
          {fetching && <Spinner size="sm" />}
        </HStack>
        
        <HStack spacing={2}>
          {canManageCalendar && (
            <Button 
              leftIcon={<AddIcon />} 
              colorScheme="blue" 
              size="sm"
              onClick={() => {
                console.log('=== DEBUG: Add Shift Button Clicked ===');
                
                // Use the calendar's selected institution
                const currentInstitution = filters.institution || user?.institution || '';
                const defaultCohort = filters.cohort || userCohortIds[0] || "";
                
                console.log('Add shift button institution resolution:');
                console.log('- filters.institution:', filters.institution);
                console.log('- user.institution:', user?.institution);
                console.log('- current institution:', currentInstitution);
                console.log('- default cohort:', defaultCohort);
                console.log('- current filters:', filters);
                
                if (!currentInstitution) {
                  console.log('No institution available, showing warning');
                  toast({
                    title: 'Institution Required',
                    description: 'Please select an institution before creating a shift.',
                    status: 'warning',
                    duration: 3000,
                  });
                  return;
                }
                
                console.log('Setting initial newShiftData for Add Shift button:');
                setNewShiftData(prev => {
                  const updated = {
                    ...prev,
                    institution: currentInstitution,
                    cohort: defaultCohort,
                    // Clear previous selections to force fresh loading
                    agencyId: '',
                    clinicalSiteId: '',
                  };
                  
                  console.log('Add shift button newShiftData updated:', updated);
                  return updated;
                });
                
                console.log('Opening Add Shift modal...');
                onAddShiftOpen();
              }}
            >
              Add Shift
            </Button>
          )}
          <Button
            leftIcon={<ArrowBackIcon />}
            size="sm"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Button
            leftIcon={<FiRefreshCw />}
            colorScheme="cyan"
            size="sm"
            onClick={() => handleManualSync()}
            isLoading={syncFetching}
            isDisabled={!selectedCohortId && !canSeeAllInstitutions}
            title={!selectedCohortId ? "Sync all enabled FISDAP cohorts" : "Refresh the selected cohort from FISDAP"}
          >
            Sync FISDAP
          </Button>
          <Menu>
            <MenuButton
              as={IconButton}
              size="sm"
              icon={<DeleteIcon />}
              aria-label="Reset synced shifts"
              isDisabled={!selectedCohortId || syncFetching}
              variant="outline"
              title={!selectedCohortId ? "Select a specific cohort before resetting synced shifts" : "Reset and resync the selected cohort"}
            />
            <MenuList>
              <MenuItem
                icon={<DeleteIcon />}
                onClick={() => handleManualSync({ clearExisting: true })}
              >
                Reset selected cohort and resync
              </MenuItem>
            </MenuList>
          </Menu>
          <Menu>
            <MenuButton as={Button} size="sm" leftIcon={<ExternalLinkIcon />} variant="outline">
              Export
            </MenuButton>
            <MenuList>
              <MenuItem onClick={downloadICal}>Download iCal</MenuItem>
              <MenuItem onClick={exportToGoogleCalendar}>Google Calendar</MenuItem>
              <MenuItem onClick={exportToOutlook}>Outlook Calendar</MenuItem>
              <MenuItem 
                onClick={sendCalendarToEmail}
                isDisabled={emailFetching}
                icon={emailFetching ? <Spinner size="sm" /> : undefined}
              >
                {emailFetching ? 'Sending...' : 'Send to Email'}
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </HStack>
      
      {/* Persistent Filters Bar */}
      <Box bg={useColorModeValue('gray.50', 'gray.700')} p={3} borderRadius="md" mb={3}>
        <Flex wrap="wrap" align="center" gap={3}>
          {/* Institution Filter - Most Prominent */}
          {canSeeAllInstitutions ? (
            <FormControl minWidth="200px" maxWidth="250px">
              <FormLabel fontSize="xs" mb={1} fontWeight="bold" color="blue.600">
                Institution
              </FormLabel>
              <Select
                size="sm"
                placeholder="All Institutions"
                value={filters.institution}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  institution: e.target.value,
                  cohort: '',
                  // Clear agency and clinical site when institution changes
                  agency: '',
                  clinicalSite: '',
                  student: '',
                }))}
                bg={useColorModeValue('white', 'gray.700')}
                color="gray.800"
                borderColor="blue.300"
                _hover={{ borderColor: 'blue.400' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
              >
                {allUsersFetching ? (
                  <option disabled>Loading...</option>
                ) : (
                  getAvailableInstitutions().map(institution => (
                    <option key={institution} value={institution}>
                      {institution}
                    </option>
                  ))
                )}
              </Select>
            </FormControl>
          ) : (
            <Box minWidth="200px">
              <Text fontSize="xs" mb={1} fontWeight="bold" color="blue.600">
                Institution
              </Text>
              <Box
                bg={useColorModeValue('white', 'gray.600')}
                px={3}
                py={2}
                borderRadius="md"
                border="1px solid"
                borderColor="blue.300"
                fontSize="sm"
                fontWeight="semibold"
                color={useColorModeValue('gray.800', 'gray.200')}
              >
                {filters.institution || user?.institution || 'Not Set'}
              </Box>
            </Box>
          )}
          
          <Divider orientation="vertical" height="40px" />
          
          {/* Quick Filters */}
          <ButtonGroup size="xs" variant="outline" spacing={1}>
            <Button
              colorScheme={filters.quickFilter === QUICK_FILTERS.MY_SHIFTS ? 'blue' : 'gray'}
              onClick={() => applyQuickFilter(QUICK_FILTERS.MY_SHIFTS)}
            >
              My Shifts
            </Button>
            <Button
              colorScheme={filters.quickFilter === QUICK_FILTERS.THIS_WEEK ? 'blue' : 'gray'}
              onClick={() => applyQuickFilter(QUICK_FILTERS.THIS_WEEK)}
            >
              This Week
            </Button>
            <Button
              colorScheme={filters.quickFilter === QUICK_FILTERS.THIS_MONTH ? 'blue' : 'gray'}
              onClick={() => applyQuickFilter(QUICK_FILTERS.THIS_MONTH)}
            >
              This Month
            </Button>
            <Button
              colorScheme={filters.quickFilter === QUICK_FILTERS.FISDAP ? 'cyan' : 'gray'}
              onClick={() => applyQuickFilter(QUICK_FILTERS.FISDAP)}
            >
              FISDAP
            </Button>
          </ButtonGroup>
          
          <Divider orientation="vertical" height="20px" />
          
          {/* Filter Selects */}
          
          <Select
            size="sm"
            placeholder="Cohort"
            minWidth="120px"
            maxWidth="180px"
            value={filters.cohort}
            onChange={(e) => setFilters(prev => ({ ...prev, cohort: e.target.value }))}
            bg={useColorModeValue('white', 'gray.700')}
            color="gray.800"
          >
            {getAvailableCohorts().map((cohort) => (
              <option key={cohort.id} value={cohort.id}>
                {cohort.label}
              </option>
            ))}
          </Select>

          <Select
            size="sm"
            placeholder="Source"
            minWidth="120px"
            maxWidth="150px"
            value={filters.source}
            onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value, quickFilter: e.target.value === "fisdap_ical" ? QUICK_FILTERS.FISDAP : null }))}
            bg={useColorModeValue('white', 'gray.700')}
            color="gray.800"
          >
            <option value="fisdap_ical">FISDAP</option>
            <option value="manual">Manual</option>
          </Select>

          <Select
            size="sm"
            placeholder="Student"
            minWidth="170px"
            maxWidth="230px"
            value={filters.student}
            onChange={(e) => setFilters(prev => ({ ...prev, student: e.target.value }))}
            bg={useColorModeValue('white', 'gray.700')}
            color="gray.800"
            disabled={shiftDocumentationsFetching}
          >
            {studentOptions.map((student) => (
              <option key={student.id} value={student.id}>
                {getStudentDisplayName(student)}
              </option>
            ))}
          </Select>
          
          <Box position="relative">
            <Select
              size="sm"
              placeholder="Shift Type"
              minWidth="140px"
              maxWidth="180px"
              value={filters.shiftType}
              onChange={(e) => setFilters(prev => ({ ...prev, shiftType: e.target.value }))}
              bg={useColorModeValue('white', 'gray.700')}
              color="gray.800"
            >
              <option value="">All Types</option>
              <option value="field">🚑 Field</option>
              <option value="hospital">🏥 Hospital</option>
              <option value="lab">🧪 Lab</option>
              <option value="field_internship">🏁 Field Internship</option>
            </Select>
            {filters.shiftType && (
              <Box
                position="absolute"
                right="32px"
                top="50%"
                transform="translateY(-50%)"
                width="8px"
                height="8px"
                borderRadius="50%"
                bg={`${SHIFT_TYPE_COLORS[filters.shiftType]}.500`}
                pointerEvents="none"
                zIndex={2}
              />
            )}
          </Box>
          
          <Select
            size="sm"
            placeholder="Agency"
            minWidth="150px"
            maxWidth="200px"
            value={filters.agency}
            onChange={(e) => setFilters(prev => ({ ...prev, agency: e.target.value }))}
            disabled={agenciesFetching}
            bg={useColorModeValue('white', 'gray.700')}
            color="gray.800"
          >
            {agenciesFetching ? (
              <option disabled>Loading...</option>
            ) : (
              getAvailableAgencies().map(agency => (
                <option key={agency.id} value={agency.id}>
                  {agency.name}
                </option>
              ))
            )}
          </Select>
          
          <Select
            size="sm"
            placeholder="Clinical Site"
            minWidth="150px"
            maxWidth="200px"
            value={filters.clinicalSite}
            onChange={(e) => setFilters(prev => ({ ...prev, clinicalSite: e.target.value }))}
            disabled={clinicalSitesFetching}
            bg={useColorModeValue('white', 'gray.700')}
            color="gray.800"
          >
            {clinicalSitesFetching ? (
              <option disabled>Loading...</option>
            ) : (
              getAvailableClinicalSites().map(site => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))
            )}
          </Select>
          
          <Select
            size="sm"
            placeholder="Status"
            minWidth="120px"
            maxWidth="150px"
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            bg={useColorModeValue('white', 'gray.700')}
            color="gray.800"
          >
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </Select>
          
          {/* Clear Filters */}
          {(filters.shiftType || filters.status || filters.cohort || filters.agency || filters.clinicalSite || filters.student || filters.source || filters.quickFilter) && (
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setFilters(prev => ({
                institution: prev.institution, // Keep institution when clearing other filters
                cohort: '',
                shiftType: '',
                status: '',
                agency: '',
                clinicalSite: '',
                student: '',
                source: '',
                quickFilter: null,
              }))}
            >
              Clear Filters
            </Button>
          )}
        </Flex>
      </Box>
      
      {/* Navigation and View Controls */}
      <HStack justify="space-between" mb={3} spacing={4}>
        <HStack spacing={3}>
          <HStack spacing={1}>
            <IconButton
              icon={<ChevronLeftIcon />}
              onClick={() => navigateCalendar(-1)}
              size="sm"
              variant="outline"
            />
            <Button onClick={goToToday} size="sm" variant="outline">
              Today
            </Button>
            <IconButton
              icon={<ChevronRightIcon />}
              onClick={() => navigateCalendar(1)}
              size="sm"
              variant="outline"
            />
          </HStack>
          
          <Heading size="md">{formatDateHeader()}</Heading>
        </HStack>
        
        <ButtonGroup size="sm" variant="outline">
          <Button
            colorScheme={view === VIEWS.MONTH ? 'blue' : 'gray'}
            onClick={() => setView(VIEWS.MONTH)}
          >
            Month
          </Button>
          <Button
            colorScheme={view === VIEWS.WEEK ? 'blue' : 'gray'}
            onClick={() => setView(VIEWS.WEEK)}
          >
            Week
          </Button>
          <Button
            colorScheme={view === VIEWS.DAY ? 'blue' : 'gray'}
            onClick={() => setView(VIEWS.DAY)}
          >
            Day
          </Button>
          <Button
            colorScheme={view === VIEWS.LIST ? 'blue' : 'gray'}
            onClick={() => setView(VIEWS.LIST)}
          >
            List
          </Button>
        </ButtonGroup>
      </HStack>
      
      {/* Calendar Content */}
      <Box minH="600px" bg={useColorModeValue('gray.50', 'gray.800')} borderRadius="md" p={3}>
        {renderCalendarGrid()}
      </Box>
      
      {/* Add Shift Modal */}
      <Modal isOpen={isAddShiftOpen} onClose={onAddShiftClose} size="xl">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent bg={bgColor} maxW="900px">
          <ModalHeader>Create New Shift</ModalHeader>
          <ModalCloseButton />
          <ModalBody p={6}>
            <VStack spacing={6} align="stretch">
              <Grid templateColumns="repeat(2, 1fr)" gap={6} minChildWidth="200px">
                <FormControl isRequired>
                  <FormLabel>Agency</FormLabel>
                  <Select
                    placeholder="Select agency"
                    value={newShiftData.agencyId}
                    onChange={(e) => {
                      console.log('=== DEBUG: Agency Selected ===');
                      console.log('Selected agency ID:', e.target.value);
                      setNewShiftData(prev => ({ ...prev, agencyId: e.target.value }));
                    }}
                    disabled={agenciesFetching}
                    bg="white"
                    color="gray.800"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                    _disabled={{ 
                      bg: 'gray.100', 
                      color: 'gray.500',
                      cursor: 'not-allowed' 
                    }}
                    sx={{
                      option: {
                        bg: 'white',
                        color: 'gray.800'
                      }
                    }}
                  >
                    {(() => {
                      // SPECIFIC DEBUG LOGGING FOR AGENCY DROPDOWN RENDERING
                      console.log('=== DEBUG: Agency Dropdown Specific Debug ===');
                      console.log('1. Current newShiftData.institution value:', newShiftData.institution);
                      
                      const availableAgenciesResult = getAvailableAgencies(newShiftData.institution);
                      console.log('2. Result of getAvailableAgencies(newShiftData.institution):', availableAgenciesResult);
                      console.log('   - Count:', availableAgenciesResult.length);
                      console.log('   - Agency names:', availableAgenciesResult.map(a => a.name));
                      
                      const targetInstitution = newShiftData.institution || filters.institution;
                      console.log('3. Target institution being used for filtering:', targetInstitution);
                      console.log('   - From newShiftData.institution:', newShiftData.institution);
                      console.log('   - From filters.institution:', filters.institution);
                      
                      console.log('4. Agency filtering correctness check:');
                      if (agencies && agencies.length > 0) {
                        const totalAgencies = agencies.length;
                        const filteredAgencies = availableAgenciesResult.length;
                        const filteringWorking = targetInstitution ? filteredAgencies < totalAgencies : filteredAgencies === totalAgencies;
                        console.log('   - Total agencies in system:', totalAgencies);
                        console.log('   - Agencies after filtering:', filteredAgencies);
                        console.log('   - Is filtering working correctly?', filteringWorking);
                        
                        if (targetInstitution) {
                          console.log('   - Filtering by institution:', targetInstitution);
                          console.log('   - Expected: fewer agencies than total');
                          console.log('   - Actual: filtering', filteringWorking ? 'IS' : 'IS NOT', 'working');
                        } else {
                          console.log('   - No institution filter, should show all agencies');
                          console.log('   - All agencies', filteringWorking ? 'ARE' : 'ARE NOT', 'being shown');
                        }
                      } else {
                        console.log('   - No agencies data available for filtering check');
                      }
                      console.log('=== END Agency Dropdown Specific Debug ===');
                      
                      console.log('=== DEBUG: Agency Select Rendering (DETAILED) ===');
                      console.log('Current timestamp:', new Date().toISOString());
                      console.log('newShiftData.institution:', newShiftData.institution);
                      console.log('agenciesFetching:', agenciesFetching);
                      console.log('Raw agencies data:', agencies);
                      console.log('Total agencies count:', agencies?.length || 0);
                      
                      // Log each agency's institutionalAffiliations data with more detail
                      if (agencies && agencies.length > 0) {
                        console.log('--- DETAILED AGENCY ANALYSIS ---');
                        agencies.forEach((agency, index) => {
                          console.log(`\n${index + 1}. Agency: "${agency.name}"`);
                          console.log(`   ID: ${agency.id}`);
                          console.log(`   institution field: "${agency.institution}"`);
                          console.log(`   institutionalAffiliations:`, agency.institutionalAffiliations);
                          console.log(`   institutionalAffiliations type:`, typeof agency.institutionalAffiliations);
                          console.log(`   institutionalAffiliations is array:`, Array.isArray(agency.institutionalAffiliations));
                          
                          if (Array.isArray(agency.institutionalAffiliations)) {
                            console.log(`   institutionalAffiliations length:`, agency.institutionalAffiliations.length);
                            console.log(`   institutionalAffiliations contents:`, agency.institutionalAffiliations);
                            agency.institutionalAffiliations.forEach((affiliation, affIndex) => {
                              console.log(`     [${affIndex}]: "${affiliation}" (type: ${typeof affiliation})`);
                              console.log(`     Exact match with "${newShiftData.institution}":`, affiliation === newShiftData.institution);
                              console.log(`     Includes check:`, agency.institutionalAffiliations.includes(newShiftData.institution));
                            });
                          }
                          
                          // Test the filtering logic step by step
                          let shouldInclude = false;
                          let reason = '';
                          
                          if (!newShiftData.institution) {
                            shouldInclude = true;
                            reason = 'No institution filter - including all';
                          } else if (agency.institutionalAffiliations && Array.isArray(agency.institutionalAffiliations)) {
                            shouldInclude = agency.institutionalAffiliations.includes(newShiftData.institution);
                            reason = `institutionalAffiliations ${shouldInclude ? 'includes' : 'does not include'} "${newShiftData.institution}"`;
                          } else {
                            shouldInclude = false;
                            reason = 'No institution data available';
                          }
                          
                          console.log(`   FILTER RESULT: ${shouldInclude ? 'INCLUDE' : 'EXCLUDE'} - ${reason}`);
                        });
                        console.log('--- END DETAILED AGENCY ANALYSIS ---\n');
                      } else {
                        console.log('No agencies data available or empty array');
                      }
                      
                      // Now test the actual filtering function
                      console.log('\n=== TESTING getAvailableAgencies FUNCTION ===');
                      const availableAgencies = getAvailableAgencies(newShiftData.institution);
                      console.log('Function input - institution:', newShiftData.institution);
                      console.log('Function result - available agencies:', availableAgencies);
                      console.log('Function result - count:', availableAgencies.length);
                      
                      if (availableAgencies.length > 0) {
                        console.log('Available agencies details:');
                        availableAgencies.forEach((agency, index) => {
                          console.log(`  ${index + 1}. ${agency.name} (ID: ${agency.id})`);
                        });
                      }
                      console.log('=== END FUNCTION TEST ===\n');
                      
                      // CRITICAL DEBUG: Final agency dropdown state
                      console.log('🔍 FINAL AGENCY DROPDOWN DEBUG:');
                      console.log('newShiftData.institution value:', newShiftData.institution);
                      console.log('typeof newShiftData.institution:', typeof newShiftData.institution);
                      console.log('Raw agencies array:', agencies);
                      console.log('agenciesFetching status:', agenciesFetching);

                      // Test Memphis Fire specifically
                      const memphisFireAgency = agencies?.find(a => a.name === 'Memphis Fire');
                      if (memphisFireAgency) {
                        console.log('✅ Memphis Fire found in raw data:');
                        console.log('  - ID:', memphisFireAgency.id);
                        console.log('  - institutionalAffiliations:', memphisFireAgency.institutionalAffiliations);
                        console.log('  - includes STCC?:', memphisFireAgency.institutionalAffiliations?.includes('STCC'));
                        console.log('  - includes newShiftData.institution?:', memphisFireAgency.institutionalAffiliations?.includes(newShiftData.institution));
                      } else {
                        console.log('❌ Memphis Fire NOT found in raw agencies data');
                      }

                      // Test the filtering function directly
                      const filteredResult = getAvailableAgencies(newShiftData.institution);
                      console.log('Available agencies result:', filteredResult);
                      console.log('Memphis Fire in filtered result?:', filteredResult.find(a => a.name === 'Memphis Fire'));
                      
                      if (agenciesFetching) {
                        return <option disabled>Loading agencies...</option>;
                      } else if (!newShiftData.institution) {
                        return <option disabled>Please select an institution first</option>;
                      } else if (availableAgencies.length === 0) {
                        console.log('=== DEBUG: NO AGENCIES FOUND - FINAL ANALYSIS ===');
                        console.log('Institution filter:', newShiftData.institution);
                        console.log('Total agencies in system:', agencies?.length || 0);
                        console.log('After filtering:', availableAgencies.length);
                        console.log('This indicates no agencies match the current institution filter');
                        return <option disabled>No agencies available for {newShiftData.institution}</option>;
                      } else {
                        return availableAgencies.map(agency => (
                          <option key={agency.id} value={agency.id}>
                            {agency.name}
                          </option>
                        ));
                      }
                    })()}
                  </Select>
                </FormControl>
                
                <FormControl>
                  <FormLabel>Clinical Site (Optional)</FormLabel>
                  <Select
                    placeholder="Select clinical site"
                    value={newShiftData.clinicalSiteId}
                    onChange={(e) => {
                      console.log('=== DEBUG: Clinical Site Selected ===');
                      console.log('Selected clinical site ID:', e.target.value);
                      setNewShiftData(prev => ({ ...prev, clinicalSiteId: e.target.value }));
                    }}
                    disabled={allClinicalSitesFetching || clinicalSitesFetching}
                    bg="white"
                    color="gray.800"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                    _disabled={{ 
                      bg: 'gray.100', 
                      color: 'gray.500',
                      cursor: 'not-allowed' 
                    }}
                    sx={{
                      option: {
                        bg: 'white',
                        color: 'gray.800'
                      }
                    }}
                  >
                    {(() => {
                      console.log('=== DEBUG: Clinical Site Select Rendering ===');
                      console.log('newShiftData.institution:', newShiftData.institution);
                      console.log('allClinicalSitesFetching:', allClinicalSitesFetching);
                      console.log('clinicalSitesFetching:', clinicalSitesFetching);
                      const availableSites = getAvailableClinicalSites(newShiftData.institution);
                      console.log('Available clinical sites:', availableSites);
                      console.log('Clinical sites error:', clinicalSitesError);
                      
                      const isLoading = allClinicalSitesFetching || clinicalSitesFetching;
                      
                      if (isLoading) {
                        return <option disabled style={{ color: 'gray' }}>Loading clinical sites...</option>;
                      } else if (clinicalSitesError) {
                        return <option disabled style={{ color: 'gray' }}>Error loading clinical sites</option>;
                      } else if (!newShiftData.institution) {
                        return <option disabled style={{ color: 'gray' }}>Please select an institution first</option>;
                      } else if (availableSites.length === 0) {
                        return <option disabled style={{ color: 'gray' }}>No clinical sites available for {newShiftData.institution}</option>;
                      } else {
                        return availableSites.map(site => (
                          <option key={site.id} value={site.id} style={{ color: 'black', backgroundColor: 'white' }}>
                            {site.name}
                          </option>
                        ));
                      }
                    })()}
                  </Select>
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel>Location</FormLabel>
                  <Input
                    placeholder="Enter location"
                    value={newShiftData.location}
                    onChange={(e) => setNewShiftData(prev => ({ ...prev, location: e.target.value }))}
                    bg="white"
                    color="gray.800"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Unit/Ward/Station (Optional)</FormLabel>
                  <Input
                    placeholder="Enter unit/department"
                    value={newShiftData.unit}
                    onChange={(e) => setNewShiftData(prev => ({ ...prev, unit: e.target.value }))}
                    bg="white"
                    color="gray.800"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel>Shift Date</FormLabel>
                  <Input
                    type="datetime-local"
                    value={newShiftData.shiftDate}
                    onChange={(e) => setNewShiftData(prev => ({ ...prev, shiftDate: e.target.value }))}
                    bg="white"
                    color="gray.800"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>End Time</FormLabel>
                  <Input
                    type="datetime-local"
                    value={newShiftData.endTime}
                    onChange={(e) => setNewShiftData(prev => ({ ...prev, endTime: e.target.value }))}
                    bg="white"
                    color="gray.800"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Enrollment Start</FormLabel>
                  <Input
                    type="datetime-local"
                    value={newShiftData.enrollmentPeriodStart}
                    onChange={(e) => setNewShiftData(prev => ({ ...prev, enrollmentPeriodStart: e.target.value }))}
                    bg="white"
                    color="gray.800"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Enrollment End</FormLabel>
                  <Input
                    type="datetime-local"
                    value={newShiftData.enrollmentPeriodEnd}
                    onChange={(e) => setNewShiftData(prev => ({ ...prev, enrollmentPeriodEnd: e.target.value }))}
                    bg="white"
                    color="gray.800"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Max Students</FormLabel>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={newShiftData.maxStudents}
                    onChange={(e) => setNewShiftData(prev => ({ ...prev, maxStudents: e.target.value }))}
                    bg="white"
                    color="gray.800"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  />
                </FormControl>
              </Grid>
              
              <Divider />
              
              <VStack align="stretch" spacing={3}>
                <Checkbox
                  isChecked={newShiftData.requiresApproval}
                  onChange={(e) => setNewShiftData(prev => ({ ...prev, requiresApproval: e.target.checked }))}
                >
                  Requires Approval?
                </Checkbox>
                
                <Checkbox
                  isChecked={newShiftData.addStudent}
                  onChange={(e) => setNewShiftData(prev => ({ ...prev, addStudent: e.target.checked, selectedStudents: [] }))}
                >
                  Add Student?
                </Checkbox>
                
                {newShiftData.addStudent && newShiftData.cohort && (
                  <FormControl>
                    <FormLabel>Select Student</FormLabel>
                    <Select
                      placeholder="Select student"
                      value={newShiftData.selectedStudents[0] || ''}
                      onChange={(e) => setNewShiftData(prev => ({ 
                        ...prev, 
                        selectedStudents: e.target.value ? [e.target.value] : [] 
                      }))}
                      bg="white"
                      color="gray.800"
                      borderColor="gray.300"
                      _hover={{ borderColor: 'gray.400' }}
                      _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                      sx={{
                        option: {
                          bg: 'white',
                          color: 'gray.800'
                        }
                      }}
                    >
                      {[...(users?.filter((candidate) => {
                        if (!newShiftData.cohort) return false;
                        const selectedCohort = String(newShiftData.cohort);
                        const candidateCohortId = String(candidate?.cohort?.id || "");
                        const candidateCohortName = String(candidate?.cohort?.name || "");
                        return selectedCohort === candidateCohortId || selectedCohort === candidateCohortName;
                      }) || [])]
                        .sort((a, b) => {
                          const aLast = String(a?.lastName || '').toLowerCase();
                          const bLast = String(b?.lastName || '').toLowerCase();
                          if (aLast !== bLast) return aLast.localeCompare(bLast);

                          const aFirst = String(a?.firstName || '').toLowerCase();
                          const bFirst = String(b?.firstName || '').toLowerCase();
                          if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);

                          const aEmail = String(a?.email || '').toLowerCase();
                          const bEmail = String(b?.email || '').toLowerCase();
                          return aEmail.localeCompare(bEmail);
                        })
                        .map(student => (
                        <option key={student.id} value={student.id} style={{ color: 'black', backgroundColor: 'white' }}>
                          {student.firstName} {student.lastName}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </VStack>
              
              <Box p={4} bg={useColorModeValue('blue.50', 'blue.900')} borderRadius="md" border="1px solid" borderColor={useColorModeValue('blue.200', 'blue.700')}>
                <Text fontSize="sm" color={useColorModeValue('blue.800', 'blue.200')}>
                  <strong>Institution:</strong> {newShiftData.institution || 'Not Set'}
                </Text>
                <Text fontSize="sm" color={useColorModeValue('blue.800', 'blue.200')} mt={1}>
                  <strong>Display Preview:</strong> {newShiftData.location}{newShiftData.unit ? ` - ${newShiftData.unit}` : ''}
                </Text>
                <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')} mt={1}>
                  <strong>Available Agencies:</strong> {getAvailableAgencies(newShiftData.institution).length} found
                  {newShiftData.institution && ` for ${newShiftData.institution}`}
                </Text>
                <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')}>
                  <strong>Available Clinical Sites:</strong> {getAvailableClinicalSites(newShiftData.institution).length} found
                  {newShiftData.institution && ` for ${newShiftData.institution}`}
                  {(allClinicalSitesFetching || clinicalSitesFetching) && ' (loading...)'}
                  {clinicalSitesError && ' (error loading)'}
                </Text>
                {newShiftData.requiresApproval && (
                  <Text fontSize="sm" color={useColorModeValue('orange.700', 'orange.300')} mt={2}>
                    <strong>⚠️ Requires Approval</strong>
                  </Text>
                )}
                {newShiftData.addStudent && newShiftData.selectedStudents.length > 0 && (
                  <Text fontSize="sm" color={useColorModeValue('green.700', 'green.300')} mt={1}>
                    <strong>👤 Student Pre-assigned</strong>
                  </Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter bg={useColorModeValue('gray.50', 'gray.700')} borderTop="1px solid" borderTopColor={borderColor}>
            <Button variant="ghost" mr={3} onClick={onAddShiftClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleCreateShift}
              isLoading={createFetching}
            >
              Create Shift
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Shift Detail Modal */}
      <Modal isOpen={isShiftDetailOpen} onClose={onShiftDetailClose} size="xl">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent bg={bgColor} maxW="800px">
          <ModalHeader>Shift Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedShift && (
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Heading size="md">{selectedShift.location}</Heading>
                  <Tag colorScheme={STATUS_COLORS[selectedShift.status] || 'gray'}>
                    {selectedShift.status}
                  </Tag>
                </HStack>
                
                <Divider />
                
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <Box>
                    <Text fontWeight="semibold" color="gray.600">Date</Text>
                    <Text>
                      {new Date(selectedShift.shiftDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" color="gray.600">Time</Text>
                    <Text>
                      {new Date(selectedShift.shiftDate).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: false
                      })}
                      {selectedShift.endTime && ` - ${new Date(selectedShift.endTime).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: false
                      })}`}
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" color="gray.600">Type</Text>
                    <Tag colorScheme={SHIFT_TYPE_COLORS[selectedShift.shiftType] || 'gray'}>
                      {selectedShift.shiftType}
                    </Tag>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" color="gray.600">Location</Text>
                    <Text>{selectedShift.location}</Text>
                  </Box>
                  
                  {selectedShift.unit && (
                    <Box>
                      <Text fontWeight="semibold" color="gray.600">Unit/Department</Text>
                      <Text>{selectedShift.unit}</Text>
                    </Box>
                  )}
                  
                  {selectedShift.agency && (
                    <Box>
                      <Text fontWeight="semibold" color="gray.600">Agency</Text>
                      <Text>{selectedShift.agency.name}</Text>
                    </Box>
                  )}
                  
                  {selectedShift.clinicalSite && (
                    <Box>
                      <Text fontWeight="semibold" color="gray.600">Clinical Site</Text>
                      <Text>
                        {selectedShift.clinicalSite.name}
                        {selectedShift.clinicalSite.units && Array.isArray(selectedShift.clinicalSite.units) && selectedShift.clinicalSite.units.length > 0 &&
                          ` (${selectedShift.clinicalSite.units.map(formatClinicalSiteUnit).filter(Boolean).join(', ')})`
                        }
                      </Text>
                    </Box>
                  )}

                  {selectedShift.syncSource === "fisdap_ical" && (
                    <Box>
                      <Text fontWeight="semibold" color="gray.600">FISDAP</Text>
                      <Text fontSize="sm">{selectedShift.fisdapEventUid || 'Synced shift'}</Text>
                      {selectedShift.lastSyncedAt && (
                        <Text fontSize="xs" color="gray.500">
                          Last synced {new Date(selectedShift.lastSyncedAt).toLocaleString()}
                        </Text>
                      )}
                    </Box>
                  )}
                  
                  <Box>
                    <Text fontWeight="semibold" color="gray.600">Institution</Text>
                    <Text>{selectedShift.institution}</Text>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" color="gray.600">Cohort</Text>
                    <Text>{selectedShift.cohort?.name || 'N/A'}</Text>
                  </Box>
                  
                  {selectedShift.preceptor && (
                    <Box>
                      <Text fontWeight="semibold" color="gray.600">Preceptor</Text>
                      <Text 
                        color={canAccessShiftDetails() ? "blue.600" : "inherit"}
                        cursor={canAccessShiftDetails() ? "pointer" : "default"}
                        _hover={canAccessShiftDetails() ? { 
                          color: "blue.800", 
                          textDecoration: "underline" 
                        } : {}}
                        onClick={() => canAccessShiftDetails() && handleNameClick(selectedShift.id)}
                      >
                        {selectedShift.preceptor.firstName} {selectedShift.preceptor.lastName}
                      </Text>
                    </Box>
                  )}
                </Grid>

                {getShiftStudentDocs(selectedShift).length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" color="gray.600" mb={2}>Students</Text>
                    <VStack align="stretch" spacing={1}>
                      {getShiftStudentDocs(selectedShift).map((doc) => (
                        <Flex key={doc.id} justify="space-between" gap={3}>
                          <Text>{getStudentDisplayName(doc.student)}</Text>
                          <HStack spacing={2}>
                            <Badge>{doc.enrollmentStatus}</Badge>
                            <Badge colorScheme={doc.completionStatus === "complete" ? "green" : "gray"}>
                              {doc.completionStatus}
                            </Badge>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                )}
                
                {selectedShift.locked && (
                  <Box>
                    <Tag colorScheme="red" size="sm">
                      <Text fontSize="xs">512 Locked</Text>
                    </Tag>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter bg={useColorModeValue('gray.50', 'gray.700')} borderTop="1px solid" borderTopColor={borderColor}>
            {/* Delete button for admins */}
            {selectedShift && user && hasAnyRole(user?.roles, ['admin', 'system-admin']) && (
              <Button 
                colorScheme="red" 
                variant="outline"
                leftIcon={<DeleteIcon />}
                onClick={() => handleDeleteShift(selectedShift.id)}
                isLoading={deleteFetching}
                mr="auto"
              >
                Delete Shift
              </Button>
            )}
            
            {/* Claim button for students */}
            {selectedShift && canClaimShift(selectedShift) && (
              <Button
                colorScheme="green"
                leftIcon={<AddIcon />}
                onClick={() => {
                  handleClaimShift(selectedShift);
                  onShiftDetailClose();
                }}
                isLoading={claimFetching}
                mr={3}
              >
                Claim Shift
              </Button>
            )}
            
            {/* View Details button for authorized users */}
            {selectedShift && canAccessShiftDetails() && (
              <Button
                colorScheme="blue"
                onClick={() => {
                  handleNameClick(selectedShift.id);
                  onShiftDetailClose();
                }}
                mr={3}
              >
                View Details
              </Button>
            )}
            
            <Button onClick={onShiftDetailClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
